import React, { useState } from "react";
import {
  ArrowUpFromDot,
  BoxSelect,
  ChevronDown,
  ChevronUp,
  CircleDot,
  Grid3X3,
  MapPin,
  RotateCcw,
  ScanEye,
  Sparkles,
  X,
} from "lucide-react";
import { unitRgbToHex } from "../../utils/colorUtils";
import AxisNumberInput from "../ui/AxisNumberInput";
import Button from "../ui/Button";
import Tooltip from "../ui/Tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import { InspectorVisibilityEyeButton } from "../ui/InspectorVisibilityEyeButton";
import { InspectorVisibilitySection } from "./InspectorVisibilitySection";
import VisibilityIconRow from "./VisibilityIconRow";

const MULTI_SELECTION_CLEAR_BUTTON_CLASS =
  "inline-flex items-center justify-center rounded border border-gray-600 bg-transparent px-2.5 py-1 text-xs font-medium text-gray-300 transition-colors hover:border-gray-500 hover:bg-gray-700 hover:text-gray-100 focus-visible:border-gray-500 focus-visible:bg-gray-700 focus-visible:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300/40";
const MULTI_SELECTION_WARNING_CLASS =
  "rounded bg-amber-500/10 px-2 py-1.5 text-xs text-amber-400/90";
const ORIGIN_DRAG_PROPS = { deferDragUpdates: false };

function MultiSelectionSummary({ label, items, secondary, onRemoveItem }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="min-w-0 flex-1">
      <button
        type="button"
        data-no-panel-drag="true"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-left text-gray-300 transition-colors hover:text-gray-100"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 shrink-0 panel-text-helper" aria-hidden />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 panel-text-helper" aria-hidden />
        )}
        <span className="truncate">{label}</span>
      </button>
      {secondary ? (
        <p className="ml-6 mt-0.5 truncate text-xs panel-text-helper">{secondary}</p>
      ) : null}
      {expanded ? (
        <div className="ml-6 mt-1 max-h-32 overflow-auto rounded border border-gray-700/70 py-1">
          {items.length > 0 ? (
            items.map((item) => (
              <Tooltip key={item.id} content={item.label} fullWidth>
                <div className="group flex min-w-0 items-center gap-2 px-2 py-1 text-xs text-gray-300 transition-colors hover:bg-white/5 hover:text-gray-100">
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {onRemoveItem ? (
                    <Tooltip content="Remove from selection">
                      <button
                        type="button"
                        data-no-panel-drag="true"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemoveItem(item.id);
                        }}
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded panel-text-helper opacity-0 transition-colors hover:bg-white/10 hover:text-gray-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400/70 group-hover:opacity-100"
                        aria-label={`Remove ${item.label} from selection`}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </Tooltip>
                  ) : null}
                </div>
              </Tooltip>
            ))
          ) : (
            <div className="px-2 py-0.5 text-xs panel-text-helper">No selection</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function InspectorMultiBodySelectionPanel({
  allVisible,
  childCount,
  isPhysicsRunning,
  multiBoundingBox,
  multiFocus,
  multiHighlight,
  onClearSelection,
  onRemoveBody,
  onToggleBoundingBox,
  onToggleFocus,
  onToggleHighlight,
  onToggleVisibility,
  selectedBodies,
  selectedBodySummaryItems,
}) {
  const visibilityIconActions = [
    {
      key: "highlight",
      title: "Highlight",
      on: multiHighlight,
      onClick: onToggleHighlight,
      disabled: isPhysicsRunning,
      Icon: Sparkles,
    },
    {
      key: "focus",
      title: "Display selected only",
      on: multiFocus,
      onClick: onToggleFocus,
      disabled: isPhysicsRunning,
      Icon: ScanEye,
    },
    {
      key: "boundingBox",
      title: "Bounding box",
      on: multiBoundingBox,
      onClick: onToggleBoundingBox,
      disabled: isPhysicsRunning,
      Icon: BoxSelect,
    },
  ];

  return (
    <div className="flex flex-1 flex-col gap-3 p-4 text-sm">
      <div className="flex items-start justify-between gap-2">
        <MultiSelectionSummary
          label={`${selectedBodies.length} bodies selected`}
          items={selectedBodySummaryItems}
          onRemoveItem={onRemoveBody}
        />
        <button
          type="button"
          onClick={onClearSelection}
          className={`${MULTI_SELECTION_CLEAR_BUTTON_CLASS} shrink-0`}
        >
          Clear selection
        </button>
      </div>
      {childCount > 0 && (
        <div className={MULTI_SELECTION_WARNING_CLASS}>
          {childCount === 1
            ? "1 selected body is a child of another selected body - it will follow its parent and won't move independently."
            : `${childCount} selected bodies are children of other selected bodies - they will follow their parents and won't move independently.`}
        </div>
      )}
      <InspectorVisibilitySection
        sectionTitle="Visibility"
        headerActions={
          <InspectorVisibilityEyeButton
            visible={allVisible}
            onToggle={isPhysicsRunning ? undefined : onToggleVisibility}
          />
        }
      >
        <VisibilityIconRow actions={visibilityIconActions} />
      </InspectorVisibilitySection>
    </div>
  );
}

export function InspectorMultiMeshSelectionPanel({
  clearSelectedVisualMeshes,
  handleMultiMeshAssignMaterial,
  handleMultiMeshOriginDeltaChange,
  handleMultiMeshResetOrigins,
  handleMultiMeshSetCenterOrigin,
  handleMultiMeshSetStartOrigin,
  handleMultiMeshToggleVisibility,
  isPhysicsRunning,
  meshVisibility,
  multiMeshBoundingBox,
  multiMeshHighlight,
  multiMeshOriginMarker,
  multiMeshWireframe,
  multiMeshWireframeColor,
  selectedVisualMeshCount,
  selectedVisualMeshIds,
  selectedVisualMeshMaterialIds,
  selectedVisualMeshMaterialLabel,
  selectedVisualMeshSummaryItems,
  selectedVisualMeshTransformState,
  setMultiMeshBoundingBox,
  setMultiMeshHighlight,
  setMultiMeshOriginMarker,
  setMultiMeshWireframe,
  setMultiMeshWireframeColor,
  visualMeshMaterialOptions,
  onRemoveVisualMesh,
}) {
  const [originDelta, setOriginDelta] = useState([0, 0, 0]);
  const [originDeltaInputs, setOriginDeltaInputs] = useState({});
  const originDeltaRef = React.useRef(originDelta);
  const resetOriginDelta = React.useCallback(() => {
    originDeltaRef.current = [0, 0, 0];
    setOriginDelta([0, 0, 0]);
    setOriginDeltaInputs({});
  }, []);
  const selectedVisualMeshUuids = Array.from(selectedVisualMeshIds);
  const allSelectedVisualMeshesVisible = selectedVisualMeshUuids.every(
    (meshUuid) => meshVisibility[meshUuid] !== false
  );
  React.useEffect(() => {
    resetOriginDelta();
  }, [resetOriginDelta, selectedVisualMeshIds]);
  const originControlsDisabled = isPhysicsRunning || !selectedVisualMeshTransformState.canTransform;
  const updateOriginDeltaAxis = React.useCallback(
    (axisIndex, value) => {
      if (originControlsDisabled || typeof handleMultiMeshOriginDeltaChange !== "function") return;
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return;
      const previous = originDeltaRef.current;
      const increment = numericValue - (previous[axisIndex] || 0);
      const next = [...previous];
      next[axisIndex] = numericValue;
      originDeltaRef.current = next;
      setOriginDelta(next);
      if (Math.abs(increment) > 1e-12) {
        const delta = [0, 0, 0];
        delta[axisIndex] = increment;
        handleMultiMeshOriginDeltaChange(delta);
      }
    },
    [handleMultiMeshOriginDeltaChange, originControlsDisabled]
  );
  const handleSetCenterOrigin = React.useCallback(() => {
    if (originControlsDisabled || typeof handleMultiMeshSetCenterOrigin !== "function") return;
    handleMultiMeshSetCenterOrigin();
    resetOriginDelta();
  }, [handleMultiMeshSetCenterOrigin, originControlsDisabled, resetOriginDelta]);
  const handleSetStartOrigin = React.useCallback(() => {
    if (originControlsDisabled || typeof handleMultiMeshSetStartOrigin !== "function") return;
    handleMultiMeshSetStartOrigin();
    resetOriginDelta();
  }, [handleMultiMeshSetStartOrigin, originControlsDisabled, resetOriginDelta]);
  const handleResetOrigins = React.useCallback(() => {
    if (originControlsDisabled || typeof handleMultiMeshResetOrigins !== "function") return;
    handleMultiMeshResetOrigins();
    resetOriginDelta();
  }, [handleMultiMeshResetOrigins, originControlsDisabled, resetOriginDelta]);
  const meshVisibilityIconActions = [
    {
      key: "highlight",
      title: "Highlight selected visuals",
      on: multiMeshHighlight,
      onClick: () => setMultiMeshHighlight((value) => !value),
      disabled: isPhysicsRunning,
      Icon: Sparkles,
    },
    {
      key: "wireframe",
      title: "Wireframe overlay",
      on: multiMeshWireframe,
      onClick: () => setMultiMeshWireframe((value) => !value),
      disabled: isPhysicsRunning,
      Icon: Grid3X3,
    },
    {
      key: "bbox",
      title: "Bounding box",
      on: multiMeshBoundingBox,
      onClick: () => setMultiMeshBoundingBox((value) => !value),
      disabled: isPhysicsRunning,
      Icon: BoxSelect,
    },
    {
      key: "origin",
      title: "Mesh origin marker (dot + positive-axis L + up arrow)",
      on: multiMeshOriginMarker,
      onClick: () => setMultiMeshOriginMarker((value) => !value),
      disabled: isPhysicsRunning,
      Icon: ArrowUpFromDot,
    },
  ];

  return (
    <div className="flex flex-1 flex-col gap-3 p-4 text-sm">
      <div className="flex items-start justify-between gap-2">
        <MultiSelectionSummary
          label={`${selectedVisualMeshCount} visuals selected`}
          items={selectedVisualMeshSummaryItems}
          secondary={selectedVisualMeshMaterialLabel}
          onRemoveItem={onRemoveVisualMesh}
        />
        <button
          type="button"
          onClick={clearSelectedVisualMeshes}
          className={`${MULTI_SELECTION_CLEAR_BUTTON_CLASS} shrink-0`}
        >
          Clear selection
        </button>
      </div>
      {!selectedVisualMeshTransformState.canTransform && selectedVisualMeshTransformState.reason ? (
        <div className={MULTI_SELECTION_WARNING_CLASS}>
          {selectedVisualMeshTransformState.reason}
        </div>
      ) : null}
      <InspectorVisibilitySection
        sectionTitle="Visibility"
        headerActions={
          <InspectorVisibilityEyeButton
            visible={allSelectedVisualMeshesVisible}
            onToggle={isPhysicsRunning ? undefined : handleMultiMeshToggleVisibility}
            tooltipWhenOn="Selected visuals are visible - click to hide"
            tooltipWhenOff="Some selected visuals are hidden - click to show"
            ariaLabel="Toggle selected visual mesh visibility"
          />
        }
      >
        <VisibilityIconRow actions={meshVisibilityIconActions} />
        {multiMeshWireframe && !isPhysicsRunning ? (
          <div className="flex items-center gap-2 px-0.5">
            <span className="shrink-0 select-none text-xs text-gray-400">Wireframe color</span>
            <input
              type="color"
              value={multiMeshWireframeColor}
              onChange={(event) => setMultiMeshWireframeColor(event.target.value)}
              className="h-7 w-9 cursor-pointer rounded border border-gray-600 bg-transparent"
              aria-label="Wireframe color"
            />
          </div>
        ) : null}
      </InspectorVisibilitySection>

      <Collapsible>
        <CollapsibleTrigger quiet>Edit Mesh Origins</CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3">
            <div className="text-xs panel-text-helper">
              Applies the same origin-position delta to each selected mesh while keeping the
              selected body geoms in place.
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="small"
                className="gap-1.5"
                onClick={handleSetCenterOrigin}
                disabled={originControlsDisabled}
                title={
                  originControlsDisabled
                    ? selectedVisualMeshTransformState.reason || "Mesh origins cannot be edited"
                    : "Set each selected mesh origin to that mesh's visible center"
                }
              >
                <CircleDot className="h-3.5 w-3.5" />
                <span>Center</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="small"
                className="gap-1.5"
                onClick={handleSetStartOrigin}
                disabled={originControlsDisabled}
                title={
                  originControlsDisabled
                    ? selectedVisualMeshTransformState.reason || "Mesh origins cannot be edited"
                    : "Set each selected mesh origin to the closest point on that mesh's bounds"
                }
              >
                <MapPin className="h-3.5 w-3.5" />
                <span>Start</span>
              </Button>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-gray-300">Origin Position Delta</div>
              <div className="grid grid-cols-3 gap-2">
                {["X", "Y", "Z"].map((axis, index) => {
                  const editingKey = `multi-origin-delta-${index}`;
                  return (
                    <AxisNumberInput
                      key={editingKey}
                      axis={axis}
                      value={
                        originDeltaInputs[editingKey] !== undefined
                          ? originDeltaInputs[editingKey]
                          : (originDelta[index] || 0).toFixed(3)
                      }
                      dragValue={originDelta[index] || 0}
                      {...ORIGIN_DRAG_PROPS}
                      onDragValueChange={(nextValue) => updateOriginDeltaAxis(index, nextValue)}
                      onChange={(event) => {
                        const textValue = event.target.value;
                        setOriginDeltaInputs((prev) => ({ ...prev, [editingKey]: textValue }));
                        const numericValue = Number.parseFloat(textValue);
                        if (Number.isFinite(numericValue)) {
                          updateOriginDeltaAxis(index, numericValue);
                        }
                      }}
                      onFocus={(event) => {
                        setOriginDeltaInputs((prev) => ({
                          ...prev,
                          [editingKey]: event.target.value,
                        }));
                      }}
                      onBlur={(event) => {
                        const numericValue = Number.parseFloat(event.target.value);
                        updateOriginDeltaAxis(
                          index,
                          Number.isFinite(numericValue) ? numericValue : 0
                        );
                        setOriginDeltaInputs((prev) => {
                          const next = { ...prev };
                          delete next[editingKey];
                          return next;
                        });
                      }}
                      disabled={originControlsDisabled}
                      labelClassName="w-3 text-center text-xs text-gray-300"
                      inputClassName="w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100"
                      inputProps={{ step: "0.001" }}
                    />
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="small"
                className="gap-1.5"
                onClick={handleResetOrigins}
                disabled={originControlsDisabled}
                title={
                  originControlsDisabled
                    ? selectedVisualMeshTransformState.reason || "Mesh origins cannot be edited"
                    : "Reset selected mesh origins"
                }
              >
                <RotateCcw className="h-3.5 w-3.5" />
                <span>Reset Origins</span>
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div>
        <div className="mb-2 text-sm text-gray-200">Material</div>
        <div className="flex flex-wrap gap-2">
          {visualMeshMaterialOptions.length > 0 ? (
            visualMeshMaterialOptions.map((material) => {
              const materialId = material.uuid;
              const label = material.name || materialId;
              let hex = "#ffffff";
              if (Array.isArray(material?.color)) {
                hex = unitRgbToHex(material.color, [1, 1, 1]);
              }
              const isSelected =
                selectedVisualMeshMaterialIds.size === 1 &&
                selectedVisualMeshMaterialIds.has(materialId);
              return (
                <Tooltip key={materialId} content={label}>
                  <button
                    onClick={() => handleMultiMeshAssignMaterial(materialId)}
                    disabled={isPhysicsRunning}
                    className={`flex items-center gap-2 rounded bg-[rgba(30,30,30,0.85)] py-1 pl-1.5 pr-3 outline-none transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
                      isSelected
                        ? "border border-white font-semibold text-white"
                        : "border border-[#444] font-normal text-gray-200 hover:border-gray-400 hover:text-white"
                    }`}
                    aria-label={label}
                    type="button"
                  >
                    <span
                      className="inline-block h-4 w-4 shrink-0 rounded-full border border-[#888]"
                      style={{ backgroundColor: hex }}
                    />
                    <span className="whitespace-nowrap text-sm">{label}</span>
                  </button>
                </Tooltip>
              );
            })
          ) : (
            <span className="text-xs text-gray-400">Loading...</span>
          )}
        </div>
      </div>
    </div>
  );
}
